import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Wrench, Activity, Sparkles, AlertTriangle, Plane, Loader2, RefreshCw,
  TrendingUp, Clock, ShieldCheck, ListChecks
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const RISK_STYLE = {
  critical: { bg: 'bg-rose-600/15', border: 'border-rose-600/40', text: 'text-rose-300', dot: 'bg-rose-500' },
  high:     { bg: 'bg-orange-500/15', border: 'border-orange-500/40', text: 'text-orange-300', dot: 'bg-orange-500' },
  medium:   { bg: 'bg-amber-500/15', border: 'border-amber-500/40', text: 'text-amber-300', dot: 'bg-amber-500' },
  low:      { bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', text: 'text-emerald-300', dot: 'bg-emerald-500' },
};

export default function PredictiveMaintenancePage() {
  const { t } = useTranslation();
  const [aircraftList, setAircraftList] = useState([]);
  const [selectedAircraft, setSelectedAircraft] = useState('');
  const [aircraftPrediction, setAircraftPrediction] = useState(null);
  const [fleet, setFleet] = useState(null);
  const [loadingFleet, setLoadingFleet] = useState(false);
  const [loadingAnalyze, setLoadingAnalyze] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [activeTab, setActiveTab] = useState('fleet');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailComponent, setDetailComponent] = useState(null);

  const token = localStorage.getItem('token');

  const fetchAircraftList = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await fetch(`${API_BASE}/api/predictive-maintenance/aircraft-list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAircraftList(data.aircraft || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingList(false);
    }
  }, [token]);

  const fetchFleet = useCallback(async () => {
    setLoadingFleet(true);
    try {
      const res = await fetch(`${API_BASE}/api/predictive-maintenance/fleet-risk?top_n=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setFleet(await res.json());
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingFleet(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAircraftList();
    fetchFleet();
  }, [fetchAircraftList, fetchFleet]);

  const analyzeAircraft = async (aircraftId) => {
    if (!aircraftId) {
      toast.error('Select an aircraft first');
      return;
    }
    setLoadingAnalyze(true);
    setAircraftPrediction(null);
    try {
      const res = await fetch(`${API_BASE}/api/predictive-maintenance/analyze/${aircraftId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || `Analysis failed (${res.status})`);
      }
      const data = await res.json();
      setAircraftPrediction(data);
      toast.success(`Analyzed ${data.aircraft_registration || aircraftId}`);
      fetchAircraftList();
      fetchFleet();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingAnalyze(false);
    }
  };

  const openDetail = (component) => {
    setDetailComponent(component);
    setDetailOpen(true);
  };

  const RiskBadge = ({ level, score }) => {
    const style = RISK_STYLE[level] || RISK_STYLE.low;
    return (
      <Badge className={`${style.bg} ${style.text} border ${style.border} flex items-center gap-1.5 capitalize`}>
        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
        {level}{score != null && ` · ${score}`}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/30">
                <Wrench className="w-6 h-6 text-amber-300" />
              </div>
              <h1 className="text-3xl font-bold text-white tracking-tight" data-testid="pm-title">
                {t('predictiveMaintenance.title', 'Predictive Maintenance')}
              </h1>
              <Badge className="bg-violet-600/20 text-violet-300 border border-violet-600/40">
                <Sparkles className="w-3 h-3 mr-1" /> GPT-5.2 Powered
              </Badge>
            </div>
            <p className="text-slate-400 max-w-2xl">
              {t('predictiveMaintenance.subtitle',
                'AI-driven failure-risk forecasts for fleet components, derived from defects, work orders, AD compliance and life-limit signals.')}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => { fetchFleet(); fetchAircraftList(); }}
            className="border-slate-700 text-slate-200"
            data-testid="pm-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> {t('common.refresh', 'Refresh')}
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="fleet" data-testid="pm-tab-fleet">
              <TrendingUp className="w-4 h-4 mr-2" /> {t('predictiveMaintenance.fleetRisk', 'Fleet Risk')}
            </TabsTrigger>
            <TabsTrigger value="aircraft" data-testid="pm-tab-aircraft">
              <Plane className="w-4 h-4 mr-2" /> {t('predictiveMaintenance.byAircraft', 'By Aircraft')}
            </TabsTrigger>
          </TabsList>

          {/* FLEET RISK TAB */}
          <TabsContent value="fleet" className="space-y-4">
            {/* Distribution stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard
                icon={<Plane className="w-4 h-4" />}
                label="Aircraft analyzed"
                value={fleet?.aircraft_count ?? 0}
                tone="text-sky-300"
              />
              <StatCard
                icon={<ListChecks className="w-4 h-4" />}
                label="Components"
                value={fleet?.total_components_analyzed ?? 0}
                tone="text-slate-200"
              />
              {['critical', 'high', 'medium'].map(lvl => (
                <StatCard
                  key={lvl}
                  icon={<AlertTriangle className="w-4 h-4" />}
                  label={`${lvl.charAt(0).toUpperCase()}${lvl.slice(1)} risk`}
                  value={fleet?.distribution?.[lvl] ?? 0}
                  tone={RISK_STYLE[lvl].text}
                />
              ))}
            </div>

            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2 text-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  {t('predictiveMaintenance.topComponents', 'Top at-risk components fleet-wide')}
                </CardTitle>
                <CardDescription>
                  {fleet?.generated_at ? `Updated ${new Date(fleet.generated_at).toLocaleString()}` : 'Run aircraft analyses to populate'}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {loadingFleet ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                  </div>
                ) : !fleet?.top_components?.length ? (
                  <EmptyState
                    icon={<Wrench className="w-10 h-10 text-slate-600" />}
                    title="No predictions yet"
                    description="Open the 'By Aircraft' tab and run an analysis to populate the fleet view."
                  />
                ) : (
                  <ScrollArea className="max-h-[600px]">
                    <div className="divide-y divide-slate-800" data-testid="pm-fleet-list">
                      {fleet.top_components.map((c, idx) => (
                        <button
                          type="button"
                          key={`${c.aircraft_id}-${idx}`}
                          onClick={() => openDetail(c)}
                          className="w-full text-left p-4 hover:bg-slate-800/40 transition-colors"
                          data-testid={`pm-fleet-row-${idx}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-slate-100 truncate">{c.component_name}</h3>
                                {c.ata_chapter && (
                                  <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">ATA {c.ata_chapter}</Badge>
                                )}
                                <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">
                                  <Plane className="w-3 h-3 mr-1" />{c.aircraft_registration || c.aircraft_id}
                                </Badge>
                              </div>
                              <p className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                                <Clock className="w-3 h-3" /> {c.predicted_failure_window}
                              </p>
                              {c.reasoning?.[0] && (
                                <p className="text-sm text-slate-400 mt-2 line-clamp-2">{c.reasoning[0]}</p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-1.5 shrink-0">
                              <RiskBadge level={c.risk_level} score={c.risk_score} />
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* BY AIRCRAFT TAB */}
          <TabsContent value="aircraft" className="space-y-4">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">{t('predictiveMaintenance.runAnalysis', 'Run analysis for an aircraft')}</CardTitle>
                <CardDescription>Triggers GPT-5.2 to assess defects, work orders, AD compliance and life-limited components.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="flex-1 min-w-[260px]">
                    <Select value={selectedAircraft} onValueChange={setSelectedAircraft}>
                      <SelectTrigger className="bg-slate-950 border-slate-800" data-testid="pm-aircraft-select">
                        <SelectValue placeholder={loadingList ? 'Loading aircraft...' : 'Select aircraft'} />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 max-h-72">
                        {aircraftList.map(a => (
                          <SelectItem key={a.id} value={a.id}>
                            <div className="flex items-center gap-2">
                              <span className="font-mono">{a.registration}</span>
                              <span className="text-slate-500 text-xs">{a.aircraft_type || a.model}</span>
                              {a.last_prediction && (
                                <Badge className={`${RISK_STYLE[a.last_prediction.overall_risk]?.bg || ''} ${RISK_STYLE[a.last_prediction.overall_risk]?.text || ''} border ${RISK_STYLE[a.last_prediction.overall_risk]?.border || ''} text-xs ml-auto capitalize`}>
                                  {a.last_prediction.overall_risk}
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={() => analyzeAircraft(selectedAircraft)}
                    disabled={!selectedAircraft || loadingAnalyze}
                    className="bg-amber-600 hover:bg-amber-500"
                    data-testid="pm-analyze-btn"
                  >
                    {loadingAnalyze ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> {t('predictiveMaintenance.analyze', 'Run AI Analysis')}</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {aircraftPrediction && (
              <Card className="bg-slate-900/60 border-slate-800">
                <CardHeader>
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Plane className="w-5 h-5 text-sky-400" />
                        {aircraftPrediction.aircraft_registration}
                      </CardTitle>
                      <CardDescription className="mt-1">{aircraftPrediction.summary}</CardDescription>
                    </div>
                    <RiskBadge level={aircraftPrediction.overall_risk} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {aircraftPrediction.components?.length === 0 ? (
                    <p className="text-slate-500 text-sm">No component-level predictions returned.</p>
                  ) : (
                    aircraftPrediction.components.map((c, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => openDetail(c)}
                        className="w-full text-left p-3 rounded-lg border border-slate-800 hover:border-slate-700 hover:bg-slate-800/40 transition-colors"
                        data-testid={`pm-aircraft-component-${i}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-slate-100">{c.component_name}</span>
                              {c.ata_chapter && <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">ATA {c.ata_chapter}</Badge>}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{c.predicted_failure_window}</p>
                          </div>
                          <RiskBadge level={c.risk_level} score={c.risk_score} />
                        </div>
                      </button>
                    ))
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pm-detail-dialog">
          {detailComponent && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-amber-400" />
                  {detailComponent.component_name}
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Component-level failure-risk prediction
                </DialogDescription>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  {detailComponent.aircraft_registration && (
                    <span className="font-mono text-slate-300 text-sm">{detailComponent.aircraft_registration}</span>
                  )}
                  {detailComponent.ata_chapter && <Badge variant="outline" className="border-slate-700 text-slate-400">ATA {detailComponent.ata_chapter}</Badge>}
                  <RiskBadge level={detailComponent.risk_level} score={detailComponent.risk_score} />
                </div>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="flex items-center gap-2 text-slate-300">
                  <Clock className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">Predicted failure window: <strong>{detailComponent.predicted_failure_window}</strong></span>
                </div>

                <Section title="Reasoning" icon={<Activity className="w-4 h-4 text-sky-400" />}>
                  <ul className="list-disc list-inside text-slate-300 text-sm space-y-1">
                    {(detailComponent.reasoning || []).map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </Section>

                <Section title="Recommended actions" icon={<ShieldCheck className="w-4 h-4 text-emerald-400" />}>
                  <ul className="list-disc list-inside text-emerald-200 text-sm space-y-1">
                    {(detailComponent.recommended_actions || []).map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </Section>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatCard = ({ icon, label, value, tone = 'text-slate-200' }) => (
  <Card className="bg-slate-900/60 border-slate-800">
    <CardContent className="p-4">
      <div className={`flex items-center gap-2 ${tone}`}>{icon}<span className="text-xs uppercase tracking-wider opacity-80">{label}</span></div>
      <p className="text-2xl font-bold text-white mt-1 tabular-nums">{value}</p>
    </CardContent>
  </Card>
);

const Section = ({ title, icon, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-200">
      {icon}{title}
    </div>
    {children}
  </div>
);

const EmptyState = ({ icon, title, description }) => (
  <div className="flex flex-col items-center justify-center text-center py-12 px-6">
    {icon}
    <h3 className="mt-3 text-slate-300 font-semibold">{title}</h3>
    <p className="text-sm text-slate-500 max-w-md mt-1">{description}</p>
  </div>
);
